import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import OpinionChartEventItem from '../OpinionChartEventItem';
import HighPriorityEventItem from '@op/components/ChartsEvents/HighPriorityEventItem';
import HighPriorityEventBlock from '@op/components/ChartsEvents/HighPriorityEventBlock';
import type {
  IDivData,
  IChartsEventsProps,
  IHighPriorityEventItem,
  AnchorMode,
  IHighPriorityEventBlock,
} from '@op/components/ChartsEvents/types';
import { sortTimeArrayListFunc } from '@op/utils';
import {
  groupEventsToBlocks,
  isEventBlock,
} from '@op/components/ChartsEvents/utils/eventBlockUtils';
import '@op/components/ChartsEvents/index.scss';

import type { IOpinionEventFloaterDetail } from '../OpinionEventFloater';

const EVENT_VERTICAL_STACK_GAP = 6;
const EVENT_BUBBLE_UP_OFFSET = 10;

type IOpinionLineChartEventsProps = IChartsEventsProps & {
  chartRenderEventList?: Record<string, IOpinionEventFloaterDetail>;
  layoutRevision?: number;
};

export default function OpinionLineChartEvents({
  myChart,
  eventList,
  format,
  maxTimeValue,
  showEventAxis,
  dateType,
  xAxisData,
  yAxisMin,
  highPriorityEvents,
  highEventsClick,
  bottomSafeZone = 30,
  normalEventDirection = 'start',
  highPriorityEventDirection = 'start',
  blockTitle,
  trendText,
  chartRenderEventList = {},
  layoutRevision = 0,
}: IOpinionLineChartEventsProps) {
  const [renderHide, setRenderHide] = useState(true);
  const [renderRevision, setRenderRevision] = useState(0);
  // 缓存 ECharts 柱子尺寸，避免 Hover 时重复调用 API
  const chartMetricsRef = useRef({
    halfBarWidth: 0,
    barWidth: 0,
  });
  // 普通事件位置映射
  const renderPositionMap = useRef<any>({});
  // 高优先级事件位置映射
  const highPriorityPositionMap = useRef<any>({});

  function getBarInfo({ seriesName }: { seriesName: string }) {
    try {
      // 获取柱子的布局矩形
      const seriesModel = myChart.getModel().getSeriesByName(seriesName)[0];
      const layoutRect = seriesModel.getData().getItemLayout(0);
      // 计算柱子的宽度
      const barWidth = layoutRect?.width || 0;

      return {
        barWidth,
        halfBarWidth: barWidth / 2,
      };
    } catch (error) {
      return { barWidth: 0, halfBarWidth: 0 };
    }
  }

  // 更新缓存的柱子尺寸
  const updateChartMetrics = () => {
    const { halfBarWidth, barWidth } = getBarInfo({
      seriesName: 'TopTipsEvent',
    });
    chartMetricsRef.current = { halfBarWidth, barWidth };
  };

  // 图表 resize 完成后更新柱宽缓存，并使用新坐标重排气泡。
  useEffect(() => {
    if (!myChart) return;

    updateChartMetrics();
    renderPositionMap.current = {};
    highPriorityPositionMap.current = {};
    setRenderHide(true);
    setRenderRevision(revision => revision + 1);
  }, [myChart, layoutRevision]);

  /**
   * 数据预处理：合并相同时间段的高优先级事件
   */
  const processedHighPriorityEvents = useMemo(() => {
    if (!highPriorityEvents) return {};
    return groupEventsToBlocks(highPriorityEvents);
  }, [highPriorityEvents]);

  /**
   * 获取图表实际绘图区域的最大渲染高度（相对于 grid 顶部）
   * bottomSafeZone 表示从 grid 底部向上预留的空间
   */
  const getActualMaxRenderHeight = () => {
    try {
      const gridOption = myChart.getModel().getComponent('grid').option;
      const chartHeight = myChart.getHeight();

      let gridBottom = 0;

      if (gridOption.bottom) {
        if (
          typeof gridOption.bottom === 'string' &&
          gridOption.bottom.includes('%')
        ) {
          gridBottom = (parseFloat(gridOption.bottom) / 100) * chartHeight;
        } else {
          gridBottom = parseFloat(gridOption.bottom) || 0;
        }
      }

      // 事件从容器顶部往下布局，底部不超过 x 轴区域，预留 x 轴标签高度 + bottomSafeZone
      const xAxisLabelPadding = 20;
      const result =
        chartHeight - gridBottom - xAxisLabelPadding - bottomSafeZone;
      return result;
    } catch (e) {
      console.warn('[ChartsEvents] getActualMaxRenderHeight error:', e);
    }
    const fallback = myChart.getHeight() - bottomSafeZone;
    console.warn('[ChartsEvents] fallback maxRenderHeight:', fallback);
    return fallback;
  };

  /**
   * 放置板块逻辑（扫描线算法优化版）
   *
   * 使用扫描线算法将 O(n² × h) 优化为 O(n log n)
   * 按水平位置排序，维护已放置板块的水平区间，快速找到不重叠位置
   *
   * 排序依据只看 type（高优先级在前）和 left，不再把 selected 提到最前。
   * 这样选中态切换时，因为 sortedDivs 顺序与 selected 无关，
   * placeDivs 输出对每个板块的 top 都和未选中前一致 ——
   * 已经显示的板块不会因为选中而跳位或被挤掉。
   *
   * 前提：板块在选中 / 未选中两种状态下 height 应保持一致，
   * 否则 height 变化会触发扫描线重新落位。
   *
   * @param divs
   * @returns
   */
  function placeDivs(divs: IDivData[]) {
    const maxRenderHeight = getActualMaxRenderHeight();

    // 保存原始索引以便按原始顺序处理最后一个板块
    const originalLastIndex = divs.length - 1;

    // 仅按类型分组：高优先级在前，普通事件在后；同组内按 left 排序
    const highPriority = divs.filter((d) => d.type === 'high-priority');
    const normal = divs.filter((d) => d.type !== 'high-priority');
    const sortedHP = highPriority.map((div, i) => ({
      div,
      originalIndex: i,
    }));
    sortedHP.sort((a, b) => a.div.left - b.div.left);

    const sortedNormal = normal.map((div, i) => ({
      div,
      originalIndex: highPriority.length + i,
    }));
    sortedNormal.sort((a, b) => a.div.left - b.div.left);

    const sortedDivs = [...sortedHP, ...sortedNormal];

    // 维护已放置板块的水平区间
    const occupiedRows: Array<{
      top: number;
      bottom: number;
      left: number;
      right: number;
    }> = [];

    sortedDivs.forEach(({ div: currentDiv, originalIndex }) => {
      // 已经被标记为隐藏的事件（如 shouldHide），跳过布局计算
      if (currentDiv.show === false) {
        return;
      }

      // 收集与当前板块水平重叠的所有已放置板块
      const horizontalOverlaps = occupiedRows.filter(
        (row) =>
          currentDiv.left < row.right &&
          currentDiv.left + currentDiv.width > row.left,
      );

      // 如果没有水平重叠的板块，直接放在顶部
      if (horizontalOverlaps.length === 0) {
        if (currentDiv.height <= maxRenderHeight) {
          currentDiv.top = 0;
          currentDiv.show = true;
          occupiedRows.push({
            top: 0,
            bottom: currentDiv.height,
            left: currentDiv.left,
            right: currentDiv.left + currentDiv.width,
          });
        } else {
          currentDiv.show = false;
        }
        return;
      }

      // 按 top 排序
      horizontalOverlaps.sort((a, b) => a.top - b.top);

      // 找到第一个不重叠的 top 位置
      let candidateTop = 0;
      let found = false;

      // 尝试在每个间隙放置
      for (const row of horizontalOverlaps) {
        if (
          candidateTop + currentDiv.height + EVENT_VERTICAL_STACK_GAP <=
          row.top
        ) {
          // 检查是否会超出最大渲染高度
          if (candidateTop + currentDiv.height <= maxRenderHeight) {
            currentDiv.top = candidateTop;
            found = true;
            break;
          }
        }
        candidateTop = Math.max(
          candidateTop,
          row.bottom + EVENT_VERTICAL_STACK_GAP,
        );
      }

      // 如果没有找到间隙，尝试放在所有板块之后
      if (!found && candidateTop + currentDiv.height <= maxRenderHeight) {
        currentDiv.top = candidateTop;
        found = true;
      }

      if (found) {
        currentDiv.show = true;
        const divBottom = currentDiv.top! + currentDiv.height;
        if (currentDiv.height > 0) {
          console.log(
            `[ChartsEvents] placed ${currentDiv.type} top:${currentDiv.top}+h:${currentDiv.height}=${divBottom} left:${currentDiv.left}`,
          );
        }
        occupiedRows.push({
          top: currentDiv.top!,
          bottom: currentDiv.top! + currentDiv.height,
          left: currentDiv.left,
          right: currentDiv.left + currentDiv.width,
        });
      } else {
        currentDiv.show = false;
      }

      // 最后一个板块尝试减少高度进行强制放置
      if (
        originalIndex === originalLastIndex &&
        currentDiv.show === false &&
        currentDiv.type !== 'high-priority'
      ) {
        while (currentDiv.height > 40) {
          currentDiv.height = currentDiv.height - 1;

          // 重新尝试放置
          const overlaps = occupiedRows.filter(
            (row) =>
              currentDiv.left < row.right &&
              currentDiv.left + currentDiv.width > row.left,
          );

          if (overlaps.length === 0) {
            if (currentDiv.height <= maxRenderHeight) {
              currentDiv.top = 0;
              currentDiv.show = true;
              break;
            }
            continue;
          }

          overlaps.sort((a, b) => a.top - b.top);
          let testTop = 0;
          let placed = false;

          for (const row of overlaps) {
            if (
              testTop + currentDiv.height + EVENT_VERTICAL_STACK_GAP <=
              row.top
            ) {
              // 检查是否会超出最大渲染高度
              if (testTop + currentDiv.height <= maxRenderHeight) {
                currentDiv.top = testTop;
                currentDiv.show = true;
                placed = true;
                break;
              }
            }
            testTop = Math.max(testTop, row.bottom + EVENT_VERTICAL_STACK_GAP);
          }

          if (!placed && testTop + currentDiv.height <= maxRenderHeight) {
            currentDiv.top = testTop;
            currentDiv.show = true;
            placed = true;
          }

          if (placed) {
            currentDiv.height = currentDiv.height - 18;
            break;
          }
        }

        if (currentDiv.show) {
          currentDiv.isLast = true;
        }
      }
    });

    // ============ selected 抢占逻辑 ============
    // 若存在 selected 板块被常规布局隐藏，则挤掉与其水平最接近的非选中项，
    // selected 占用被挤掉者的 top，保持其他板块位置不变。
    //
    // 前置条件：
    // - selected 只会是 high-priority 类型（见 types.ts 中 IHighPriorityEventBlock.selected
    //   与 IHighPriorityEventItem.selected）
    // - 只考虑宽度能放下、但垂直方向放不下被隐藏的 selected，
    //   对于因 getEventItemInfo 超出水平边界而隐藏的（shouldHide 之类），此处也尝试抢占，
    //   但若其 left/width 无效（超出容器）则放弃。
    const hiddenSelectedDivs = divs.filter(
      (d) => d.selected === true && d.show === false,
    );

    hiddenSelectedDivs.forEach((target) => {
      // 目标被隐藏时 left / width 可能为 0（shouldHide 分支走的是占位值），
      // 这种情况下无法计算水平中心，也无法安全接管 top，直接跳过。
      if (!target.width || target.width <= 0) return;

      const targetCenter = target.left + target.width / 2;

      // 候选：已显示 + 非 selected，按与 target 的水平中心距离升序
      const candidates = divs
        .filter((d) => d.show === true && d.selected !== true)
        .map((d) => ({
          div: d,
          distance: Math.abs(d.left + d.width / 2 - targetCenter),
        }))
        .sort((a, b) => a.distance - b.distance);

      for (const { div: victim } of candidates) {
        const candidateTop = victim.top ?? 0;

        // 检查 selected 以 victim.top 放入后，是否与其他仍显示的板块发生重叠
        // （排除 victim 自身，因为即将被挤掉）
        const hasCollision = divs.some((d) => {
          if (d === victim || d === target) return false;
          if (d.show !== true) return false;
          const dTop = d.top ?? 0;
          const dBottom = dTop + d.height;
          const tBottom = candidateTop + target.height;
          const horizontalOverlap =
            target.left < d.left + d.width && target.left + target.width > d.left;
          const verticalOverlap = candidateTop < dBottom && tBottom > dTop;
          return horizontalOverlap && verticalOverlap;
        });

        // 高度是否在画布内
        const maxRenderHeight = getActualMaxRenderHeight();
        if (!hasCollision && candidateTop + target.height <= maxRenderHeight) {
          target.top = candidateTop;
          target.show = true;
          victim.show = false;
          console.log(
            `[ChartsEvents] selected preempt: selected left:${target.left} took top:${candidateTop} (victim left:${victim.left})`,
          );
          break;
        }
      }
    });

    return divs;
  }

  /**
   * 批量设置事件气泡的布局和样式
   */
  function batchSettingEventBubble(processedDivs: IDivData[]) {
    processedDivs.forEach(
      ({
        eventBox,
        eventLine,
        left,
        top,
        bottom,
        height,
        show,
        width,
        direction,
        maxHeight,
        isLast,
      }) => {
        if (show) {
          const bubbleTop = Math.max(0, (top || 0) - EVENT_BUBBLE_UP_OFFSET);

          // 设置事件气泡的位置
          eventBox.style.left = `${left}px`;
          eventBox.style.top = `${bubbleTop}px`;

          // 处理最后一个板块的高度调整
          if (isLast) {
            const eventBoxChildren: any =
              eventBox.querySelector('.dbo-event-box') ||
              eventBox.querySelector('.dbo-high-priority-wrapper') ||
              eventBox.querySelector('.dbo-high-priority-block-wrapper');
            if (eventBoxChildren) {
              eventBoxChildren.style.height = `${height}px`;
            }
          }

          // 计算线条的位置
          const lineTop = bubbleTop + height - 8;
          const lineLeft = direction === 'left' ? left : left + width - 2;
          const lineHeight = maxHeight - bubbleTop - height - bottom + 5;

          // 设置线条的样式
          eventLine.style.top = `${lineTop}px`;
          eventLine.style.left = `${lineLeft}px`;
          eventLine.style.height = `${lineHeight}px`;
          eventLine.style.display = 'block';
        } else {
          eventBox.style.display = 'none';
          eventLine.style.display = 'none';
        }
      },
    );
  }

  function getChartContentBounds(): { left: number; right: number } {
    try {
      const rect = myChart
        .getModel()
        .getComponent('grid')
        .coordinateSystem.getRect();
      return {
        left: rect.x,
        right: rect.x + rect.width,
      };
    } catch (error) {
      const width = myChart.getWidth();
      return { left: 0, right: width };
    }
  }

  function getEventItemInfo({
    eventBox,
    left,
    maxWidth,
    barWidth,
    barBottomPosition,
    maxHeight,
    eventLine,
    anchorMode = 'start',
    contentLeft = 0,
    contentRight = maxWidth,
  }: {
    eventBox: HTMLDivElement;
    left: number;
    maxWidth: number;
    barWidth: number;
    barBottomPosition: number;
    maxHeight: number;
    eventLine: HTMLDivElement;
    anchorMode?: AnchorMode;
    contentLeft?: number;
    contentRight?: number;
  }): IDivData {
    const { width, height } = eventBox.getBoundingClientRect();
    let realLeft = left;
    const direction: 'left' | 'right' =
      anchorMode === 'start' ? 'left' : 'right';
    let show = true;
    const clampRight = contentRight;
    const clampLeft = contentLeft;
    const contentWidth = clampRight - clampLeft;

    if (width > contentWidth) {
      realLeft = anchorMode === 'start' ? clampLeft : clampRight - width;
    } else if (anchorMode === 'start') {
      realLeft = left;
      if (realLeft + width > clampRight) {
        realLeft = clampRight - width;
      }
      if (realLeft < clampLeft) {
        realLeft = clampLeft;
      }
    } else {
      realLeft = left - width;
      if (realLeft < clampLeft) {
        realLeft = clampLeft;
      }
      if (realLeft + width > clampRight) {
        realLeft = clampRight - width;
      }
    }

    return {
      width,
      height,
      bottom: maxHeight - barBottomPosition,
      maxHeight,
      left: realLeft,
      eventBox,
      eventLine,
      direction,
      show,
    };
  }

  /**
   * 收集所有需要渲染的板块（普通 + 高优先级）
   * 返回带类型标记的统一数据结构
   * 以及是否有事件被选中的状态
   */
  const collectAllEventItems = useMemo(() => {
    const items: Array<{
      key: string;
      type: 'normal' | 'high-priority';
      xAxisValue: string;
      index: number;
      selected?: boolean;
      anchorMode: AnchorMode;
      endTime?: string;
      isBlock?: boolean;
      data: IHighPriorityEventItem | IHighPriorityEventBlock;
    }> = [];

    // 收集高优先级事件（已预处理，可能是单个事件或事件块）
    Object.keys(processedHighPriorityEvents || {}).forEach((xAxisValue) => {
      const renderItems = processedHighPriorityEvents![xAxisValue];
      renderItems.forEach((item, index) => {
        const blockItem = isEventBlock(item);
        const key = blockItem
          ? `block-${xAxisValue}-${index}`
          : `hp-${xAxisValue}-${index}`;
        items.push({
          key,
          type: 'high-priority',
          xAxisValue,
          index: items.length,
          selected: blockItem
            ? (item as IHighPriorityEventBlock).selected
            : (item as IHighPriorityEventItem).selected,
          anchorMode: highPriorityEventDirection,
          endTime: item.end_time,
          isBlock: blockItem,
          data: item,
        });
      });
    });

    // 收集普通事件
    Object.keys(eventList || {}).forEach((xAxisValue) => {
      const events = eventList?.[xAxisValue] || [];
      // 取所有事件中最大的 end_time
      const endTime = events.reduce((max: string | undefined, event) => {
        if (!event.end_time) return max;
        if (!max || moment(event.end_time).isAfter(moment(max))) {
          return event.end_time;
        }
        return max;
      }, undefined);

      items.push({
        key: xAxisValue,
        type: 'normal',
        xAxisValue,
        index: items.length,
        anchorMode: normalEventDirection,
        endTime,
        data: null,
      });
    });

    // 判断是否有任何事件被选中
    const hasAnySelected = items.some((item) => item.selected === true);

    // 按优先级排序：选中的事件最优先，然后高优先级在前
    const sortedItems = items.sort((a, b) => {
      // 1. 选中事件优先
      if (a.selected && !b.selected) return -1;
      if (!a.selected && b.selected) return 1;

      // 2. 高优先级在前
      if (a.type !== b.type) {
        return a.type === 'high-priority' ? -1 : 1;
      }
      return sortTimeArrayListFunc()(a.xAxisValue, b.xAxisValue);
    });

    return { items: sortedItems, hasAnySelected };
  }, [
    eventList,
    processedHighPriorityEvents,
    normalEventDirection,
    highPriorityEventDirection,
  ]);

  // 布局计算
  useEffect(() => {
    const { halfBarWidth, barWidth } = chartMetricsRef.current;
    const { left: contentLeft, right: contentRight } = getChartContentBounds();
    const chartWidth = myChart.getWidth();
    const chartHeight = myChart.getHeight();

    const allRenderList = collectAllEventItems.items
      .map((item) => {
        const positionMap =
          item.type === 'high-priority'
            ? highPriorityPositionMap.current
            : renderPositionMap.current;

        const {
          barLeftPosition,
          eventBox,
          barBottomPosition,
          eventLine,
          anchorMode,
          shouldHide,
        } = positionMap[item.key] || {};

        if (shouldHide) {
          return {
            eventBox,
            eventLine,
            show: false,
            type: item.type,
            selected: item.selected,
            width: 0,
            height: 0,
            left: 0,
            bottom: 0,
            maxHeight: 0,
            direction: 'left' as const,
          };
        }

        if (barLeftPosition == null || !eventBox) return undefined;

        const buildInfo = (mode: AnchorMode) =>
          getEventItemInfo({
            eventBox,
            left:
              mode === 'end'
                ? barLeftPosition + halfBarWidth
                : barLeftPosition - halfBarWidth,
            maxWidth: chartWidth,
            maxHeight: chartHeight,
            barWidth,
            barBottomPosition,
            eventLine,
            anchorMode: mode,
            contentLeft,
            contentRight,
          });

        let info = buildInfo(anchorMode);
        if (!info.show && anchorMode === 'end') {
          info = buildInfo('start');
        }
        if (!info.show && anchorMode === 'start') {
          info = buildInfo('end');
        }

        return {
          ...info,
          type: item.type,
          selected: item.selected,
        };
      })
      .filter((item) => item !== undefined);
    // 统一布局计算
    const processedDivs = placeDivs(allRenderList);
    // 批量设置样式
    batchSettingEventBubble(processedDivs);
  }, [renderHide, collectAllEventItems]);

  /**
   * 普通事件渲染数据
   */
  const normalEventListRender = useMemo(() => {
    const list = Object.keys(eventList || {});
    list.sort(sortTimeArrayListFunc());
    return list.map((xAxisValue) => {
      const list = eventList?.[xAxisValue];
      // 取所有事件中最大的 end_time
      const endTime = (list || []).reduce((max: string | undefined, event) => {
        if (!event.end_time) return max;
        if (!max || moment(event.end_time).isAfter(moment(max))) {
          return event.end_time;
        }
        return max;
      }, undefined);
      return {
        key: xAxisValue,
        children: list,
        anchorMode: normalEventDirection,
        endTime,
      };
    });
  }, [eventList, normalEventDirection]);

  /**
   * 高优先级事件渲染数据（支持事件块）
   */
  const highPriorityEventListRender = useMemo(() => {
    const items: Array<{
      key: string;
      xAxisValue: string;
      data: IHighPriorityEventItem | IHighPriorityEventBlock;
      anchorMode: AnchorMode;
      isBlock: boolean;
    }> = [];
    Object.keys(processedHighPriorityEvents || {}).forEach((xAxisValue) => {
      const renderItems = processedHighPriorityEvents![xAxisValue];
      renderItems.forEach((item, index) => {
        const blockItem = isEventBlock(item);
        items.push({
          key: blockItem
            ? `block-${xAxisValue}-${index}`
            : `hp-${xAxisValue}-${index}`,
          xAxisValue,
          data: item,
          anchorMode: highPriorityEventDirection,
          isBlock: blockItem,
        });
      });
    });

    return items;
  }, [processedHighPriorityEvents, highPriorityEventDirection]);

  return (
    <>
      {/* 高优先级事件（单个事件或事件块） */}
      {highPriorityEventListRender.map(
        ({ key, xAxisValue, data, anchorMode, isBlock }) => {
          // 事件块渲染
          if (isBlock) {
            return (
              <HighPriorityEventBlock
                myChart={myChart}
                xAxisValue={xAxisValue}
                block={data as IHighPriorityEventBlock}
                key={`${key}-${renderRevision}`}
                hide={renderHide}
                format={format}
                dateType={dateType}
                onBlockClick={(block) => {
                  // 点击事件块时，触发第一个事件的回调（或可以根据需求自定义）
                  highEventsClick?.(block.events[0]);
                }}
                maxTimeValue={maxTimeValue}
                yAxisMin={yAxisMin}
                showEventAxis={showEventAxis}
                halfBarWidth={chartMetricsRef.current.halfBarWidth}
                xAxisData={xAxisData}
                hasAnySelected={collectAllEventItems.hasAnySelected}
                anchorMode={
                  highPriorityPositionMap.current[key]?.anchorMode ?? anchorMode
                }
                blockTitle={blockTitle}
                backPositionInfo={(item) => {
                  highPriorityPositionMap.current = {
                    ...highPriorityPositionMap.current,
                    [key]: item,
                  };

                  // 当所有板块位置信息收集完成后，触发布局计算
                  const totalNormal = Object.keys(eventList || {}).length;
                  const totalHighPriority = highPriorityEventListRender.length;
                  const totalCollected =
                    Object.keys(renderPositionMap.current).length +
                    Object.keys(highPriorityPositionMap.current).length;

                  if (totalCollected === totalNormal + totalHighPriority) {
                    setRenderHide(false);
                  }
                }}
                trendText={trendText}
              />
            );
          }
          // 单个事件渲染
          return (
            <HighPriorityEventItem
              myChart={myChart}
              xAxisValue={xAxisValue}
              event={data as IHighPriorityEventItem}
              key={`${key}-${renderRevision}`}
              hide={renderHide}
              format={format}
              dateType={dateType}
              onHighPriorityClick={highEventsClick}
              maxTimeValue={maxTimeValue}
              yAxisMin={yAxisMin}
              showEventAxis={showEventAxis}
              halfBarWidth={chartMetricsRef.current.halfBarWidth}
              xAxisData={xAxisData}
              hasAnySelected={collectAllEventItems.hasAnySelected}
              anchorMode={
                highPriorityPositionMap.current[key]?.anchorMode ?? anchorMode
              }
              trendText={trendText}
              backPositionInfo={(item) => {
                highPriorityPositionMap.current = {
                  ...highPriorityPositionMap.current,
                  [key]: item,
                };

                // 当所有板块位置信息收集完成后，触发布局计算
                const totalNormal = Object.keys(eventList || {}).length;
                const totalHighPriority = highPriorityEventListRender.length;
                const totalCollected =
                  Object.keys(renderPositionMap.current).length +
                  Object.keys(highPriorityPositionMap.current).length;

                if (totalCollected === totalNormal + totalHighPriority) {
                  setRenderHide(false);
                }
              }}
            />
          );
        },
      )}

      {/* 普通事件 */}
      {normalEventListRender.map(
        ({ key, children = [], anchorMode, endTime }) => {
          return (
            <OpinionChartEventItem
              myChart={myChart}
              xAxisValue={key}
              eventList={children}
              key={`${key}-${renderRevision}`}
              hide={renderHide}
              format={format}
              dateType={dateType}
              maxTimeValue={maxTimeValue}
              showEventAxis={showEventAxis}
              halfBarWidth={chartMetricsRef.current.halfBarWidth}
              xAxisData={xAxisData}
              yAxisMin={yAxisMin}
              hasAnySelected={collectAllEventItems.hasAnySelected}
              anchorMode={anchorMode}
              endTime={endTime}
              chartRenderEventDetail={chartRenderEventList?.[key]}
              backPositionInfo={(item) => {
                renderPositionMap.current = {
                  ...renderPositionMap.current,
                  [key]: item,
                };

                // 当所有板块位置信息收集完成后，触发布局计算
                const totalNormal = Object.keys(eventList || {}).length;
                const totalHighPriority = highPriorityEventListRender.length;
                const totalCollected =
                  Object.keys(renderPositionMap.current).length +
                  Object.keys(highPriorityPositionMap.current).length;

                if (totalCollected === totalNormal + totalHighPriority) {
                  setRenderHide(false);
                }
              }}
            />
          );
        },
      )}
    </>
  );
}
